import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCpu, FiShield, FiZap, FiCode, FiArrowRight, FiCheckCircle, FiLayout } = FiIcons;

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to HDAI",
      description: "We're going to set up your clinical development environment in under 60 seconds.",
      icon: FiZap,
      image: "https://images.unsplash.com/photo-1576091160550-217359f4ecf8?auto=format&fit=crop&q=80&w=800",
      color: "bg-brand-mint/20"
    },
    {
      title: "Choose Your Engine",
      description: "Select the default AI brain for your clinical workflows. You can always change this later.",
      options: [
        { id: 'venice', name: 'Venice-Clinical v4', desc: 'SOTA medical reasoning (Cloud)', icon: FiCpu },
        { id: 'hf', name: 'HF Clinical', desc: 'Mistral-7B Optimized (Local/Private)', icon: FiLayout }
      ]
    },
    {
      title: "Compliance First",
      description: "Enable automated audit logging and HIPAA-ready storage for all clinical inferences.",
      features: [
        "Immutable Audit Trail",
        "E2E Encrypted Record Vault",
        "Clinician-in-the-Loop Hooks"
      ],
      icon: FiShield,
      color: "bg-blue-50"
    },
    {
      title: "Ready to Build",
      description: "Your workspace is ready. You can start by describing a workflow in plain English.",
      icon: FiCheckCircle,
      image: "https://images.unsplash.com/photo-1551288049-bbbda5366991?auto=format&fit=crop&q=80&w=800",
      color: "bg-brand-indigo text-brand-mint"
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-brand-ivory flex items-center justify-center p-6">
      <div className="max-w-4xl w-full bg-white rounded-[3rem] shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-[600px]">
        {/* Left Side: Visual */}
        <div className={`w-full md:w-1/2 p-12 flex flex-col justify-center items-center relative overflow-hidden ${steps[currentStep].color || 'bg-brand-indigo'}`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="relative z-10 w-full"
            >
              {steps[currentStep].image ? (
                <img 
                  src={steps[currentStep].image} 
                  alt="Onboarding" 
                  className="rounded-3xl shadow-xl w-full h-64 object-cover border-4 border-white/20"
                />
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-32 h-32 bg-white rounded-[2.5rem] flex items-center justify-center shadow-2xl mb-8">
                    <SafeIcon icon={steps[currentStep].icon} className="text-5xl text-brand-indigo" />
                  </div>
                  {steps[currentStep].features && (
                    <div className="space-y-4 w-full">
                      {steps[currentStep].features.map((f, i) => (
                        <div key={i} className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10 text-white font-bold text-sm flex items-center space-x-3">
                          <SafeIcon icon={FiCheckCircle} className="text-brand-mint" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          {/* Abstract background elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />
        </div>

        {/* Right Side: Content */}
        <div className="w-full md:w-1/2 p-12 flex flex-col">
          <div className="flex-grow">
            <div className="flex items-center space-x-2 mb-8">
              {steps.map((_, i) => (
                <div 
                  key={i} 
                  className={`h-1.5 flex-grow rounded-full transition-all duration-500 ${i <= currentStep ? 'bg-brand-indigo' : 'bg-gray-100'}`}
                />
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-4xl font-display font-bold text-brand-indigo">
                  {steps[currentStep].title}
                </h2>
                <p className="text-lg text-brand-slate leading-relaxed">
                  {steps[currentStep].description}
                </p>

                {steps[currentStep].options && (
                  <div className="space-y-4 pt-4">
                    {steps[currentStep].options.map((opt) => (
                      <button 
                        key={opt.id}
                        className="w-full p-6 text-left border-2 border-gray-100 rounded-[2rem] hover:border-brand-mint hover:bg-brand-mint/5 transition-all group flex items-start space-x-4"
                      >
                        <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-brand-mint/20 transition-colors">
                          <SafeIcon icon={opt.icon} className="text-brand-indigo text-xl" />
                        </div>
                        <div>
                          <div className="font-bold text-brand-indigo">{opt.name}</div>
                          <div className="text-xs text-brand-slate">{opt.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="pt-8 flex justify-between items-center">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-sm font-bold text-gray-400 hover:text-brand-indigo transition-colors"
            >
              Skip Tutorial
            </button>
            <button 
              onClick={handleNext}
              className="bg-brand-indigo text-white px-10 py-4 rounded-2xl font-bold flex items-center space-x-3 hover:bg-brand-slate transition-all shadow-xl hover:shadow-2xl active:scale-95"
            >
              <span>{currentStep === steps.length - 1 ? 'Go to Dashboard' : 'Next Step'}</span>
              <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;